import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { PurgeResult, SeedResult } from '@/types/superadmin';

/** Invalidate everything a seeded change can affect. */
function invalidateSeedData(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['superadmin'] });
  queryClient.invalidateQueries({ queryKey: ['workspaces'] });
  queryClient.invalidateQueries({ queryKey: ['properties'] });
  queryClient.invalidateQueries({ queryKey: ['current-workspace'] });
  queryClient.invalidateQueries({ queryKey: ['action-center'] });
  queryClient.invalidateQueries({ queryKey: ['public-demo'] });
}

function seedMutation(endpoint: string) {
  return function useSeed() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async () => {
        const { data } = await api.post<SeedResult>(endpoint);
        return data;
      },
      onSuccess: () => invalidateSeedData(queryClient),
    });
  };
}

/** Wipes all non-superadmin data. Destructive, for local debugging only. */
export function useEmptyDatabase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ ok: boolean; message: string }>('/superadmin/debug/empty-db');
      return data;
    },
    onSuccess: () => invalidateSeedData(queryClient),
  });
}

/** Seeds (or refreshes) the internal QA workspaces. */
export const useCreateTestWorkspaces = seedMutation('/superadmin/debug/create-test-workspaces');

/** Seeds (or refreshes) the public read-only demo showcase. */
export const useCreateDemoWorkspaces = seedMutation('/superadmin/debug/create-demo-workspaces');

function purgeMutation(endpoint: string) {
  return function usePurge() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async () => {
        const { data } = await api.post<PurgeResult>(endpoint);
        return data;
      },
      onSuccess: () => invalidateSeedData(queryClient),
    });
  };
}

/** Hard-deletes every test workspace and its data. */
export const usePurgeTestWorkspaces = purgeMutation('/superadmin/debug/purge-test-workspaces');

/** Hard-deletes every demo workspace and its data. */
export const usePurgeDemoWorkspaces = purgeMutation('/superadmin/debug/purge-demo-workspaces');
